import { useEffect, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";

import { ChevronDown, Search, SlidersHorizontal, X } from "lucide-react";

import { DataTable, FilterBar } from "@/components/data";
import type { FilterSpec } from "@/components/data";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { applyFilters, parseFilters, type FilterValues } from "@/lib/filters";

import { FactDetail } from "./FactDetail";
import { listEveryFact, listFactKeys, messageFor, ProtexError } from "./api";
import {
  buildGroups,
  filterGroups,
  holdsEveryRecord,
  isStaleRow,
  needsAttention,
  productsIn,
  searchGroups,
  selectedRow,
  selectionFor,
  type DomainGroup,
  type FactRow,
  type Selection,
} from "./catalogue";
import { AlertCard } from "./components/AlertCard";
import { NeedsAttentionStrip } from "./components/NeedsAttentionStrip";
import { exactDate, formulaLabel, productLabel } from "./format";
import { useFactsLayout } from "./useFactsLayout";

/**
 * The server's maximum page — the size of ONE request, not the size of the read.
 *
 * Both reads on this tab go through `listEveryFact`, which keeps asking at this
 * page size until it holds the server's `total`. Deliberately the ceiling
 * rather than a smaller number: it is the fewest round trips per complete read.
 *
 * The two things this tab must get right — which keys have NO fact, and how
 * many facts need attention — are both statements about a COMPLETE set, and
 * `total` from the server is what decides whether we hold one; nothing here
 * assumes it. Past `listEveryFact`'s own page ceiling we do not, and then the
 * missing rows are not synthesised and the strip says the counts are
 * unavailable rather than under-reporting them.
 */
const PAGE_SIZE = 200;

/** `product=all` — the deliberate mode that adds a Product and a Formula column. */
const ALL_PRODUCTS = "all";

/**
 * The Facts tab: triage, then the catalogue, then one fact.
 *
 * This module renders NO `PermissionGate` of its own (I-7, AGENTS boundary 3).
 * `ShellPage` already wraps every module in the shell-owned one, so a second
 * gate here was a duplicate permission renderer — and it read `decision` to do
 * it, which is precisely the module-owned permission logic the boundary bans.
 */
export function FactsTab({ areaName }: { areaName: string }) {
  // FILTER state lives in the URL — never in useState — so a triaged,
  // product-scoped view is a link that can be pasted to a teammate. The open
  // row does not: it is local state (below), because which row you have open is
  // where you are in the page, not what you are looking at.
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const navigate = useNavigate();
  const values = parseFilters(search);

  // The IDENTITY of the open row, never bare its id. A first value drafted
  // against a synthesised Missing row replaces that row with the draft it
  // created, and an id-only selection could not survive the swap — see
  // `Selection` in `catalogue.ts`.
  const [selected, setSelected] = useState<Selection | null>(null);
  // The search box narrows as the reader types. Local state rather than the
  // URL: rewriting the route on every keystroke would fight the input, and a
  // half-typed word is not a view anyone pastes to a teammate.
  const [query, setQuery] = useState("");
  // State rather than a ref: the sheet's portal container has to be a rendered
  // element by the time the sheet mounts, and only a state update re-renders
  // with it in hand.
  const [sheetAnchor, setSheetAnchor] = useState<HTMLDivElement | null>(null);
  const { ref: layoutRef, layout, filtersFolded } = useFactsLayout<HTMLDivElement>();
  const isNarrow = layout === "narrow";
  // Only the split layout has room for the pane as a column. Everywhere else
  // it opens as a SHEET: from the right beside the table (the mock-up's
  // isolated pane), from the bottom on a phone. The stacked layout used to put
  // the pane under the whole catalogue and scroll the page down to it, so a
  // click on a fact looked like the page jumping away (KAL-410).
  const paneAsSheet = layout !== "split";

  const keys = useQuery({
    queryKey: ["protex", "fact-keys"],
    queryFn: () => listFactKeys(),
  });

  // The unfiltered read. It is NOT the per-product fact list — it is what the
  // product picker is built from and where the company-wide family (which
  // carries no product, so `?product=` can never return it) comes from.
  //
  // EVERY page of it, not the first. This was one un-paged 200-record call, and
  // two claims hang off it: whether a company-wide registry key has no fact
  // (`buildGroups` only synthesises Missing rows over a complete set) and
  // whether the counts strip may state a number at all (`catalogueComplete`).
  // Past 200 records IN TOTAL — across every product and both families, not per
  // product — `holdsEveryRecord` was false forever: the strip pinned
  // permanently on "Counts unavailable", no company-wide Missing row was ever
  // synthesised again, and the product picker silently lost the products whose
  // facts fell past the cap. `listEveryFact` reads until it holds the server's
  // `total`, and past its own ceiling still returns a short page that
  // `holdsEveryRecord` reports as incomplete — a bound, not a promise.
  const catalogue = useQuery({
    queryKey: ["protex", "facts", "all"],
    queryFn: () => listEveryFact({}, { limit: PAGE_SIZE }),
  });

  const products = productsIn(catalogue.data?.data.items ?? []);
  const selectedProduct = pickProduct(values.product, products);
  const allProducts = selectedProduct === ALL_PRODUCTS;

  // Which single product "Back to one product" returns to.
  //
  // Local state, NOT the URL, for the same reason the open row is: it is where
  // the reader WAS, not what the link is scoped to — `?product=all` has to keep
  // meaning every product when it is pasted to a teammate. The button used to
  // land on `products[0]` regardless, so a reader who compared from MCT Oil C8
  // came back to Keto Collagen; a pasted all-products link still has no history
  // to restore and falls back to that first product, which is the only case
  // where it is the right answer.
  const [lastSingleProduct, setLastSingleProduct] = useState<string | null>(null);
  useEffect(() => {
    if (selectedProduct !== null && selectedProduct !== ALL_PRODUCTS) {
      setLastSingleProduct(selectedProduct);
    }
  }, [selectedProduct]);

  // Every fact about the selected product — EVERY page of them, not the first.
  // The offset used to come from a `pageIndex` that nothing on this tab could
  // ever change: no control set it, so a product with more than one page had
  // its later rows unreachable, and — because `holdsEveryRecord` compares what
  // we hold against the SERVER's total — a counts strip stuck on "Counts
  // unavailable" for that product for good.
  //
  // In "all products" mode the unfiltered page already holds every product's
  // facts, so asking for them again product-by-product would be a second read
  // of the same rows.
  const productFacts = useQuery({
    queryKey: ["protex", "facts", "product", selectedProduct],
    queryFn: () => listEveryFact({ product: selectedProduct as string }, { limit: PAGE_SIZE }),
    enabled: selectedProduct !== null && !allProducts,
  });

  const keyList = keys.data?.data ?? [];
  const catalogueItems = catalogue.data?.data.items ?? [];
  const catalogueComplete = holdsEveryRecord(catalogue.data?.data);

  const perProductItems = allProducts ? catalogueItems : (productFacts.data?.data.items ?? []);
  // With no product to select, there is no per-product read to complete: the
  // query is disabled, so `holdsEveryRecord(undefined)` is false forever and the
  // counts strip sat permanently on "Counts unavailable" for a catalogue that
  // happens to be entirely company-wide. Nothing was unread — there was nothing
  // to read, and the catalogue's own completeness is the whole answer.
  const perProductComplete =
    allProducts || selectedProduct === null
      ? catalogueComplete
      : holdsEveryRecord(productFacts.data?.data);

  const current = products.find((product) => product.product_key === selectedProduct);
  const perProductScopes = allProducts
    ? products.map((product) => ({
        key: product.product_key,
        label: productLabel(product),
        formulaVersionId: product.formula_version_id,
      }))
    : current
      ? [
          {
            key: current.product_key,
            label: productLabel(current),
            formulaVersionId: current.formula_version_id,
          },
        ]
      : [];

  const perProductGroups = buildGroups({
    facts: perProductItems,
    keys: keyList,
    family: "per-product",
    complete: perProductComplete && perProductScopes.length > 0,
    scopes: perProductScopes,
  });

  const companyGroups = buildGroups({
    facts: catalogueItems,
    keys: keyList,
    family: "company-wide",
    complete: catalogueComplete,
    // A company-wide fact hangs off no formula version — that is what makes it
    // company-wide, not a lookup that failed.
    //
    // ONE scope, so a Missing row here is synthesised per KEY and not per
    // MARKET: a threshold approved for UK answers the key even where GCC has
    // never had one. That hole is real and is declared as gap C-5 in
    // `specs/protex-c3-authoring-module.md` — closing it needs C2 to say which
    // markets a key is expected to hold a value for, and the only market list
    // reachable from here is the one read off the facts that already exist,
    // which by construction cannot name the market whose row is absent.
    scopes: [{ key: null, label: "Every product", formulaVersionId: null }],
  });

  // Counts span BOTH families: a missing returns-window is as publishable a hole
  // as a missing fibre value. `null` when we could not read a complete set —
  // never a zero, which would claim nothing needs attention.
  //
  // THE REGISTRY IS PART OF THAT SET. A missing value is a registered key with
  // no fact, so a failed `GET /fact-keys` leaves `keyList` empty, `buildGroups`
  // synthesising no Missing rows, and the strip stating a confident "0 missing
  // values" while it has not been able to look — the exact claim this null
  // exists to prevent. `isSuccess`, not `!isError`: the counts are equally
  // unknowable while the registry read is still in flight.
  const countsKnown =
    keys.isSuccess && perProductComplete && catalogueComplete && !catalogue.isError;
  const counts = countsKnown
    ? needsAttention([...perProductGroups, ...companyGroups])
    : null;
  // A cold load is not a failure. All three reads are in flight on first paint,
  // so `countsKnown` is false and the strip greeted every operator with "we
  // could not read the full list just now" before it had tried. `isLoading`
  // rather than `isPending`: a DISABLED query is pending forever, and the
  // per-product read is disabled whenever the catalogue answers for it.
  const countsPending =
    !countsKnown && (keys.isLoading || catalogue.isLoading || productFacts.isLoading);

  const chip = typeof values.attention === "string" ? values.attention : null;
  const filters = {
    domain: asString(values.domain),
    market: asString(values.market),
    status: chip === "older" ? undefined : (chip ?? asString(values.status)),
  };
  const olderOnly = chip === "older";

  // Kept apart from the searched view so an empty result can say WHICH step
  // emptied it — the filters or the search (os-portal PR 555 review, finding 3).
  const filteredPerProduct = narrow(perProductGroups, filters, olderOnly);
  const filteredCompany = narrow(companyGroups, filters, olderOnly);
  const shownPerProduct = searchGroups(filteredPerProduct, query);
  const shownCompany = searchGroups(filteredCompany, query);
  const searching = query.trim() !== "";
  // Set by the bar itself, not by the attention chip (which feeds `status` too).
  const filtersSet = Boolean(
    asString(values.domain) || asString(values.market) || asString(values.status),
  );
  const narrowing = searching || filtersSet || chip !== null;
  // The per-product read can stop short of the server's total (the
  // `listEveryFact` ceiling). Every empty state says so too, or a fact past the
  // page reads as one that does not exist (os-portal PR 555 review, finding 1).
  const perProductTotal = allProducts
    ? (catalogue.data?.data.total ?? 0)
    : (productFacts.data?.data.total ?? 0);
  const perProductShown = perProductItems.length;
  const sectionEmpty = (
    all: DomainGroup[],
    before: DomainGroup[],
    after: DomainGroup[],
    unnarrowed: string,
  ) =>
    narrowing
      ? (emptyViewMessage({
          query,
          beforeSearch: countRows(before),
          afterSearch: countRows(after),
          registered: countRows(all),
          attention: chip !== null,
          filtersSet,
        }) ?? unnarrowed)
      : unnarrowed;
  // Everything the filters left, both families — the number beside the filters
  // is about the whole view, not about whichever section it happens to sit over.
  const shownFactCount = [...shownPerProduct, ...shownCompany].reduce(
    (sum, group) => sum + group.rows.length,
    0,
  );

  const setSearch = (next: FilterValues) => {
    // The route tree declares no `validateSearch` schema, so the router types
    // `search` as `never` for a generic `useNavigate()`. `applyFilters` is the
    // codec's own pure function that keeps the merge correct; the cast only
    // widens the type the router infers for an unvalidated search object.
    const options: Record<string, unknown> = {
      search: (prev: Record<string, unknown>) => applyFilters(prev, next),
      replace: true,
    };
    void navigate(options as never);
  };

  const listError = catalogue.isError || productFacts.isError;
  const loading = keys.isLoading || catalogue.isLoading || productFacts.isLoading;
  /**
   * Nothing registered at all — every read succeeded and neither family holds a
   * row, not even a synthesised Missing one.
   *
   * One card says so, in place of the product card, the filters, both tables
   * and the pane. The tab used to render each of those in its empty form, and
   * they talked over each other: "No product-scoped facts yet — only
   * company-wide facts are shown below", then directly below it "No
   * company-wide facts registered yet" (outcome-audit cycle 3, M5). Filters over
   * nothing and "Select a fact" beside nothing to select were the same noise.
   * `keys.isSuccess` because an unread registry is not an empty one.
   */
  const catalogueEmpty =
    !loading &&
    !listError &&
    keys.isSuccess &&
    [...perProductGroups, ...companyGroups].every((group) => group.rows.length === 0);
  /** Whether the list offers anything to open — the pane's placeholder only makes sense if so. */
  const listSelectable = !listError && !catalogueEmpty;
  // The row the pane is about — which is not always the row that was clicked:
  // a synthesised Missing row is replaced by the draft a first value creates,
  // and the selection follows it. The list's `aria-current` marker is keyed on
  // THIS, so the highlighted row and the open pane can never name different
  // records.
  const detail = selectedRow([...perProductGroups, ...companyGroups], selected);
  const select = (row: FactRow) => setSelected(selectionFor(row));

  return (
    <TooltipProvider>
      <div
        ref={layoutRef}
        data-layout={layout}
        className={
          layout === "split"
            ? "grid grid-cols-[minmax(0,1fr)_28.75rem] gap-6"
            : "grid grid-cols-1 gap-6"
        }
      >
        {/* The pane track is FIXED, not `minmax(24rem,32rem)` — a range let the
            pane resize itself as the list grew, so no column ever landed twice
            in the same place and no screenshot pair could be trusted.

            It is 28.75rem, which is **575px here, not 460px**. The brand type
            scale sets `--fs-body: 20px` on `html` (`styles/brand/colors_and_type.css`),
            so a rem in this portal is 20px, not 16px. The mock-up draws the
            pane at 460px around 13-14px body text; the same pane at the brand's
            20px body needs the same proportion of room, and 460 × (20/16) = 575.
            Read the number as "the mock-up's pane at this portal's type size" —
            hard-coding 460px would set brand-size text in a mock-up-size box.

            `min-w-0` is what keeps a wide table scrolling inside its own
            container instead of pushing the page into a horizontal scrollbar. */}
        <div className="min-w-0 space-y-6">
          <NeedsAttentionStrip
            counts={counts}
            pending={countsPending}
            // No scope sentence over an empty registry: "Across every
            // company-wide fact." above a card saying none exist is the tab
            // describing a set it has just said is empty.
            scopeLabel={
              catalogueEmpty ? null : countsScope(current ? productLabel(current) : null, allProducts)
            }
            active={chip}
            onToggle={(next) => setSearch({ ...values, attention: next, status: null })}
          />

          {!catalogueEmpty && (
          <ProductPicker
            products={products}
            selected={selectedProduct}
            previous={lastSingleProduct}
            loading={catalogue.isLoading}
            complete={catalogueComplete}
            onSelect={(next) => setSearch({ ...values, product: next })}
          />
          )}

          {/* Only the three filters the bar owns are handed to it: the product
              picker and the pressed attention chip are separate controls, and
              counting them here would make "Clear 3 filters" clear one. */}
          {!catalogueEmpty && (
          <CollapsibleFilters
            narrow={filtersFolded}
            outside={
              searching ? <ActiveSearchChip query={query} onClear={() => setQuery("")} /> : undefined
            }
          >
          <FilterBar
            layout="inline"
            filters={filterSpecs(perProductGroups, companyGroups)}
            values={{
              domain: values.domain ?? null,
              market: values.market ?? null,
              status: values.status ?? null,
            }}
            onChange={(next) => setSearch({ ...next, attention: null })}
            afterFilters={<FactSearch value={query} onChange={setQuery} />}
            // The count of what the filters LEFT, on the same line as the
            // controls that produced it — on its own line it read as a fact
            // about the catalogue rather than about this view.
            //
            // It counts BOTH tables, because both are what these filters
            // govern. The mock-up prints 7 — the rows in the table directly
            // under the filter row — but that number would go stale the moment
            // a filter changed the company-wide section it does not count. So
            // the number stays true to the filters and the words say which
            // scope it is, rather than printing a smaller number that matches
            // the picture and misdescribes the control.
            //
            // No count at all when the list failed to load: "15 facts in view"
            // printed directly above "We could not load the facts".
            trailing={
              listError
                ? undefined
                : loading
                ? "Counting…"
                : `${shownFactCount} ${shownFactCount === 1 ? "fact" : "facts"} in view`
            }
          />
          </CollapsibleFilters>
          )}

          {/* A failed registry read is never silent. The facts still render —
              they loaded — but every row title has degraded to its machine key
              and no key with no fact can be shown as Missing, so the reader is
              told that rather than left with a list that looks complete. */}
          {keys.isError && (
            // The same card as the list failure below: ink says what failed,
            // grey says what it means for the screen, and red is only the rule.
            <AlertCard
              title={<>We could not read the fact registry for {areaName}.</>}
              detail={
                <>
                  {messageFor(keys.error, areaName)} Rows without a name of their own show their
                  machine key instead of a title, no Definition can be shown, and a registered key
                  with no value cannot be listed as missing — so the counts above are unavailable
                  rather than zero.
                </>
              }
              action={
                <Button
                  variant="outline"
                  size="sm"
                  data-nonmutating
                  onClick={() => void keys.refetch()}
                >
                  Try again
                </Button>
              }
            />
          )}

          {listError ? (
            // Never an empty table on a failure — an empty table asserts "there
            // is nothing", a different and false claim.
            <LoadFailureCard
              areaName={areaName}
              error={catalogue.error ?? productFacts.error}
              onRetry={() => {
                void catalogue.refetch();
                void productFacts.refetch();
              }}
            />
          ) : catalogueEmpty ? (
            <EmptyCatalogueCard />
          ) : narrowing && !loading && shownFactCount === 0 ? (
            // One line for the whole view, not each section's "nothing
            // registered" message: the facts exist, the narrowing missed them.
            <SearchEmptyState
              message={
                emptyViewMessage({
                  query,
                  beforeSearch: countRows([...filteredPerProduct, ...filteredCompany]),
                  afterSearch: 0,
                  attention: chip !== null,
                  filtersSet,
                }) ?? FILTERS_EMPTIED
              }
              shown={perProductShown}
              total={perProductTotal}
            />
          ) : (
            <>
              <FactSection
                heading={
                  allProducts
                    ? "Per-product facts — all products"
                    : `Per-product facts — showing ${current ? productLabel(current) : "…"}`
                }
                // The card above already names the product and its formula, so
                // repeating it here is a second title for one table.
                headingHidden
                caption={
                  allProducts
                    ? "The same fact key repeats per product, so the product column is what tells the rows apart."
                    : null
                }
                groups={shownPerProduct}
                showProduct={allProducts}
                narrow={isNarrow}
                loading={loading}
                emptyMessage={sectionEmpty(
                  perProductGroups,
                  filteredPerProduct,
                  shownPerProduct,
                  "No facts registered yet. Facts appear here once a key exists in the registry.",
                )}
                selected={detail?.id ?? null}
                onSelect={select}
                total={perProductTotal}
                shown={perProductShown}
              />

              <FactSection
                heading="Every product — company-wide facts"
                caption="These do not change per product, so changing the product above does not change this section. They change per market instead."
                // The rule and the brand-coloured heading are what say "a
                // different KIND of fact starts here" — without them the
                // section reads as one more domain group in the list above.
                ruled
                // The columns here are the SAME five the section above just
                // showed — Fact / Market / Value / Status / Last checked — so
                // a second visible header row repeats labels the reader has
                // read. It is hidden, not removed: every `<th>` still exists,
                // so a screen reader still names the column of each cell.
                // Only true when the table above is ALSO product-less; with
                // all products showing, that table gains a Product column, the
                // two headers stop matching, and this one has to be visible.
                headerHidden={!allProducts}
                groups={shownCompany}
                showProduct={false}
                narrow={isNarrow}
                loading={loading}
                emptyMessage={sectionEmpty(
                  companyGroups,
                  filteredCompany,
                  shownCompany,
                  "No company-wide facts registered yet.",
                )}
                selected={detail?.id ?? null}
                onSelect={select}
                total={null}
                shown={null}
                footNote="One key, one row per market — the market column is what tells them apart, exactly as the product does above. A company-wide fact never shows a product, and never shows a blank where one would go."
              />
            </>
          )}
        </div>

        {/* The pane is a column beside the list when the tab has room for both
            (`split`), a sheet from the right over the list when it has room for
            the table only (`stacked`), and a sheet from the bottom below that
            (`narrow`) — see `useFactsLayout`. The list is not useful while
            approving on a phone.

            ONE of the two is MOUNTED — never both with one hidden by CSS. A
            `hidden` wide pane still renders a full `FactDetail`, so the sheet's
            `<Label htmlFor>` resolved against the first copy React wrote and
            tapping a label focused a `display:none` input. It also ran a second
            set of fact, audit and impact queries for every open fact. */}
        {/* Not while the list offers nothing to open. "Select a fact…" under a
            load failure or an empty registry invited a click on nothing
            (outcome-audit cycles 2 and 3, G5 / M2). A pane that is already open
            stays — it holds a correction in progress. */}
        {!paneAsSheet && (selected !== null || listSelectable) && (
          // A rule and its own padding are what make this a PANE. Without them
          // the detail sits as loose text beside the list, and an empty one
          // reads as a stray sentence rather than as a place a fact will open.
          <div className="border-l border-ks-grey-4 pl-6">
            {selected ? (
              // `key` gives a newly selected fact a FRESH detail component. React
              // otherwise keeps the pending value and source across a prop change,
              // and fact B opens holding fact A's correction.
              //
              // It is the SELECTION's id, not the resolved row's. A first value
              // drafted against a Missing row changes the row under this pane
              // from the synthesised one to the draft it just created — the
              // same fact, the same operator, mid-correction. Keying on the row
              // would remount there and throw away the typed value and the
              // source the operator is still working on.
              <FactDetail
                key={selected.id}
                row={detail}
                areaName={areaName}
                onClose={() => setSelected(null)}
              />
            ) : (
              <EmptyPaneCard />
            )}
          </div>
        )}

        {/* The sheet's portal target, and the reason it exists.

            Radix mounts a portal at `document.body` by default, which puts the
            sheet OUTSIDE the shell's `PermissionGate` — a wrapper that makes
            edit controls inert with a descendant selector on its own subtree.
            Escaping it meant that in the narrow layout, where the pane is a sheet,
            a `view`-decision person got a live value field and a live "Approve
            and publish" button, while the same pane inline was correctly inert.

            Anchoring the portal here keeps the sheet a DOM descendant of the
            gate, so one permission renderer still covers both layouts and the
            module holds no permission branch of its own (I-7, AGENTS boundary
            3). The sheet stays `position: fixed`, so it is laid out against the
            viewport exactly as before — nothing on the path from the gate to
            here creates a containing block or clips it.

            `contents` so the anchor generates no box of its own: this is a grid
            child, and an empty grid item would add a `gap-6` band under the
            list on every viewport. */}
        <div ref={setSheetAnchor} className="contents" />

        {/* Rendered only once the anchor is on screen: mounting the sheet
            before its container exists would fall back to the body portal, which
            is the escape this anchor is here to prevent. */}
        {paneAsSheet && sheetAnchor !== null && (
          <Sheet open={selected !== null} onOpenChange={(open) => !open && setSelected(null)}>
            <SheetContent
              side={isNarrow ? "bottom" : "right"}
              container={sheetAnchor}
              className={
                isNarrow
                  ? "h-[92vh] overflow-y-auto rounded-t-3xl shadow-[0_-10px_30px_rgba(87,42,153,0.12)]"
                  : // The split layout's pane track, so the pane reads the same
                    // whether it sits beside the list or slides over it.
                    "overflow-y-auto border-ks-grey-4 data-[side=right]:w-[28.75rem] data-[side=right]:max-w-[92vw] data-[side=right]:sm:max-w-[28.75rem]"
              }
            >
              {/* The grab handle. It is what says "this came up from the bottom
                  and can go back down" — without it a sheet that fills most of
                  the screen reads as a page the reader has navigated to, and the
                  only way back is a close button in a corner. */}
              {isNarrow && (
                <div
                  aria-hidden
                  className="mx-auto mt-3 h-1 w-11 shrink-0 rounded-full bg-ks-grey-3"
                />
              )}
              <SheetHeader className="pb-0">
                {/* The pane below renders the SAME identity — product line,
                    title, key, scope — so a visible title here printed it twice,
                    once in the sheet's voice and once in the pane's. Radix names
                    the dialog by this title, so it stays, unseen. */}
                <SheetTitle className="sr-only">{detail?.title ?? "Fact"}</SheetTitle>
              </SheetHeader>
              {selected && (
                <div className={isNarrow ? "px-4 pb-8" : "px-6 pb-8"}>
                  <FactDetail
                    key={selected.id}
                    row={detail}
                    areaName={areaName}
                    onClose={() => setSelected(null)}
                  />
                </div>
              )}
            </SheetContent>
          </Sheet>
        )}
      </div>
    </TooltipProvider>
  );
}

/**
 * The registry holds nothing yet — the mock-up's "nothing registered" card: a
 * centred ink headline over one grey line that says what would change it.
 *
 * The mock-up's "Open the key registry" link is left out on purpose: the portal
 * ships no key-registry route, and a link to nowhere in an empty state is worse
 * than no link (declared).
 */
function EmptyCatalogueCard() {
  return (
    <div className="rounded-ks-lg border border-ks-grey-4 px-5 py-8 text-center">
      <p className="text-sm text-ks-ink">No facts registered yet.</p>
      <p className="pt-1 text-[13px] text-ks-grey-1">
        Facts appear here once a key exists in the registry.
      </p>
    </div>
  );
}

/**
 * The search box on the filter row, right after Status (KET-6326).
 *
 * The shell's `Input` at the filter pills' height and border, so it reads as
 * one more control on the row. `data-nonmutating` on both the box and its
 * clear button: searching changes nothing, and without the marker the shell's
 * read-only gate would make the box dead for a viewer (AGENTS boundary 11).
 */
export function FactSearch({
  value,
  onChange,
}: {
  value: string;
  onChange: (next: string) => void;
}) {
  return (
    <div className="relative inline-flex items-center">
      <Search aria-hidden className="pointer-events-none absolute left-2.5 size-3.5 text-ks-grey-1" />
      <Input
        data-nonmutating
        aria-label="Search facts"
        placeholder="Search facts"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Escape") onChange("");
        }}
        className="h-8 w-52 rounded-ks-sm border-ks-grey-4 bg-ks-surface pr-7 pl-7 text-[13px] text-ks-ink placeholder:text-ks-grey-1 md:text-[13px]"
      />
      {value !== "" && (
        <button
          type="button"
          data-nonmutating
          aria-label="Clear search"
          onClick={() => onChange("")}
          className="absolute right-1.5 inline-flex size-5 items-center justify-center rounded-ks-sm text-ks-grey-1 hover:text-ks-ink"
        >
          <X aria-hidden className="size-3.5" />
        </button>
      )}
    </div>
  );
}

function noMatch(query: string): string {
  return `No facts match "${query.trim()}".`;
}

/** The Domain / Market / Status filters left nothing. */
const FILTERS_EMPTIED = "No facts match the Domain, Market and Status filters.";
/** The pressed Needs attention chip left nothing — a separate control from the bar. */
const ATTENTION_EMPTIED = "No facts match the Needs attention selection.";
/** The chip and at least one filter are both narrowing: name both, never guess one. */
const BOTH_EMPTIED = "No facts match the Domain, Market and Status filters and the Needs attention selection.";

/**
 * What an empty view says, naming the step that emptied it (os-portal PR 555 review,
 * finding 3). The search is blamed ONLY when the filters left rows and the
 * search removed them all; when the filters had already emptied the view, the
 * query is not the reason and quoting it would send the reader to fix the
 * wrong control. `null` when rows are left.
 */
export function emptyViewMessage({
  query,
  beforeSearch,
  afterSearch,
  registered,
  attention = false,
  filtersSet = false,
}: {
  query: string;
  beforeSearch: number;
  afterSearch: number;
  /** Rows before ANY narrowing. Zero means nothing is registered: not a filter's doing. */
  registered?: number;
  /** The Needs attention chip is pressed (and so may be what emptied the view). */
  attention?: boolean;
  /** At least one of Domain / Market / Status is set. */
  filtersSet?: boolean;
}): string | null {
  if (afterSearch > 0) return null;
  if (registered === 0) return null;
  if (query.trim() !== "" && beforeSearch > 0) return noMatch(query);
  if (attention) return filtersSet ? BOTH_EMPTIED : ATTENTION_EMPTIED;
  return FILTERS_EMPTIED;
}

function countRows(groups: DomainGroup[]): number {
  return groups.reduce((sum, group) => sum + group.rows.length, 0);
}

/**
 * Narrowing left nothing in either section.
 *
 * When the per-product read stopped short of the server's total, the notice
 * stays under the message: a search that misses on a partial read has not
 * shown the fact is absent, only that it is not among the facts loaded
 * (os-portal PR 555 review, finding 1).
 */
export function SearchEmptyState({
  query,
  message,
  shown,
  total,
}: {
  query?: string;
  message?: string;
  shown?: number;
  total?: number;
}) {
  return (
    <div className="rounded-ks-lg border border-ks-grey-4 px-5 py-8 text-center">
      <p className="text-sm text-ks-ink">{message ?? noMatch(query ?? "")}</p>
      {shown !== undefined && total !== undefined && shown < total && (
        <p className="pt-1 text-[13px] text-ks-grey-1">
          {truncationNotice(shown, total)}
        </p>
      )}
    </div>
  );
}

function truncationNotice(shown: number, total: number): string {
  return `Showing the first ${shown} of ${total} facts. Filters and search apply only to the facts shown.`;
}

/**
 * The active search, kept in sight on a narrow screen (os-portal PR 555 review, finding 2).
 *
 * There the search box lives inside the folded filter panel, and folding it hid
 * the query while the list stayed filtered — a short list with no visible
 * reason. This chip sits OUTSIDE the fold and carries its own clear control,
 * marked `data-nonmutating` like the box's own (AGENTS boundary 11).
 */
export function ActiveSearchChip({ query, onClear }: { query: string; onClear: () => void }) {
  return (
    <span className="inline-flex h-8 max-w-full items-center gap-1.5 rounded-ks-sm border border-ks-purple-3 bg-ks-purple-4 pr-1.5 pl-3 text-[13px] text-ks-ink">
      <Search aria-hidden className="size-3.5 shrink-0 text-ks-purple" />
      <span className="truncate">“{query.trim()}”</span>
      <button
        type="button"
        data-nonmutating
        aria-label="Clear search"
        onClick={onClear}
        className="inline-flex size-5 shrink-0 items-center justify-center rounded-ks-sm text-ks-grey-1 hover:text-ks-ink"
      >
        <X aria-hidden className="size-3.5" />
      </button>
    </span>
  );
}

/**
 * The pane before a fact is opened.
 *
 * A dashed card, not bare text: it holds the space the fact will occupy, so
 * opening one does not shove the page around, and it reads as an empty slot
 * rather than as an instruction floating beside the list.
 *
 * Exported so the screenshot harness photographs THIS card for the
 * "nothing selected" state rather than a second copy of it — the harness used
 * to render `FactDetail` with no row, which is a different state entirely
 * ("this fact is no longer in the list") and left the real empty pane unshot.
 */
export function EmptyPaneCard() {
  return (
    <div className="rounded-ks-md border border-dashed border-ks-grey-3 px-5 py-5">
      <p className="text-sm text-ks-grey-1">
        Select a fact to see its value, where it came from, and everything that has happened to it.
      </p>
    </div>
  );
}

/**
 * The filter bar, behind one button on a narrow viewport.
 *
 * Three select controls and a count take most of a phone screen above the list
 * they filter, and a person who opened PROTEX on a phone came to read or approve
 * ONE fact — the filters are the thing they reach for last. Above `lg` they are
 * the toolbar they always were: nothing is hidden on a viewport with room for it.
 *
 * A `<details>`, not a state flag: the browser already owns disclosure, gives it
 * a keyboard and a name, and the summary needs no permission marker because
 * opening a filter panel publishes nothing.
 */
export function CollapsibleFilters({
  narrow,
  outside,
  children,
}: {
  narrow: boolean;
  /** Shown beside the fold, never inside it — the state the fold must not hide. */
  outside?: ReactNode;
  children: ReactNode;
}) {
  if (!narrow) return <>{children}</>;
  return (
    <div className="flex flex-col gap-2">
      {outside && <div className="flex flex-wrap items-center gap-2">{outside}</div>}
      <details>
        <summary className="inline-flex h-8 cursor-pointer list-none items-center gap-1.5 rounded-ks-sm border border-ks-grey-4 px-3 text-[13px] text-ks-ink [&::-webkit-details-marker]:hidden">
          <SlidersHorizontal aria-hidden className="size-3.5 text-ks-grey-1" />
          Filters
        </summary>
        <div className="pt-3">{children}</div>
      </details>
    </div>
  );
}

function asString(raw: unknown): string | undefined {
  return typeof raw === "string" && raw !== "" ? raw : undefined;
}

/**
 * Which product the tab is scoped to.
 *
 * Defaults to the first product rather than to nothing: an unscoped nutrition
 * list renders the same key several times with no way to tell the rows apart,
 * which is the failure the product rule exists to prevent.
 */
export function pickProduct(
  raw: unknown,
  products: Array<{ product_key: string }>,
): string | null {
  if (raw === ALL_PRODUCTS) return ALL_PRODUCTS;
  if (typeof raw === "string" && products.some((p) => p.product_key === raw)) return raw;
  return products[0]?.product_key ?? null;
}

/**
 * Which product leaving "every product" returns to.
 *
 * The product the reader came FROM, so comparing across products and then
 * stepping back is a round trip rather than a silent jump to whichever product
 * happens to sort first. `previous` is dropped when it is no longer in the
 * catalogue (a re-read can lose a product) and when there is none to return to
 * — a pasted `?product=all` link carries no history — and the first product is
 * then the honest answer rather than a wrong one.
 *
 * Exported so the round trip is tested where it is decided: the button lives
 * behind a click that a static render can never reach.
 */
export function backToProduct(
  previous: string | null | undefined,
  products: Array<{ product_key: string }>,
): string {
  if (previous && products.some((product) => product.product_key === previous)) return previous;
  return products[0]?.product_key ?? ALL_PRODUCTS;
}

function countsScope(product: string | null, allProducts: boolean): string {
  if (allProducts) return "Across every product and every company-wide fact.";
  return product
    ? `Across ${product} and every company-wide fact.`
    : "Across every company-wide fact.";
}

function narrow(
  groups: DomainGroup[],
  filters: { domain?: string; market?: string; status?: string },
  olderOnly: boolean,
): DomainGroup[] {
  const narrowed = filterGroups(groups, filters);
  if (!olderOnly) return narrowed;
  return narrowed
    .map((group) => ({
      ...group,
      rows: group.rows.filter((row) => isStaleRow(row)),
    }))
    .filter((group) => group.rows.length > 0);
}

/**
 * Domain and market are offered as the values actually present, not as a fixed
 * enum: a filter listing an option that matches nothing is a filter that teaches
 * the reader to distrust it.
 */
function filterSpecs(...groupSets: DomainGroup[][]): FilterSpec[] {
  const groups = groupSets.flat();
  const domains = new Map(groups.map((group) => [group.domain, group.title]));
  const markets = [
    ...new Set(groups.flatMap((group) => group.rows.map((row) => row.market))),
  ].sort();
  return [
    {
      name: "domain",
      label: "Domain",
      kind: "choice",
      // "All" where the reader thinks of a complete set; "Any" where they are
      // choosing one state out of several.
      anyLabel: "All",
      options: [...domains.entries()]
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([value, label]) => ({ value, label })),
    },
    {
      name: "market",
      label: "Market",
      kind: "choice",
      anyLabel: "All",
      options: markets.map((market) => ({ value: market, label: market })),
    },
    {
      name: "status",
      label: "Status",
      kind: "choice",
      anyLabel: "Any",
      options: [
        { value: "approved", label: "Approved" },
        { value: "draft", label: "Draft — not published" },
        { value: "missing", label: "Missing" },
        { value: "superseded", label: "Superseded" },
      ],
    },
  ];
}

function ProductPicker({
  products,
  selected,
  previous,
  loading,
  complete,
  onSelect,
}: {
  products: Array<{
    product_key: string;
    product_name: string;
    formula_version_label: string | null;
    formula_effective_from: string | null;
  }>;
  selected: string | null;
  /** The single product last looked at, so leaving "every product" returns there. */
  previous?: string | null;
  loading: boolean;
  complete: boolean;
  onSelect: (productKey: string) => void;
}) {
  if (loading) return <Skeleton className="h-[86px] w-full rounded-ks-md" />;
  if (products.length === 0) {
    return (
      <p className="text-sm text-ks-grey-1">
        No product-scoped facts yet — only company-wide facts are shown below.
      </p>
    );
  }

  const all = selected === ALL_PRODUCTS;
  const current = products.find((product) => product.product_key === selected);
  const version = formulaLabel(current?.formula_version_label);
  const since = current?.formula_effective_from
    ? `in production since ${exactDate(current.formula_effective_from).split(",")[0]}`
    : null;
  // The store's own handle and SKU would belong on this line, but the facts
  // endpoint carries neither — and a made-up identifier beside a real formula
  // version is worse than a shorter line.
  const subLine = [version, since].filter(Boolean).join(", ");

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-4 rounded-ks-md border border-ks-purple-3 bg-ks-purple-4 px-4 py-3.5">
        {/* The product is not a form field among others — it is what the whole
            list below is ABOUT, so it reads as a stated context with the
            control inside it, not as a bare select with a floating label.

            IT MUST STILL READ AS A CONTROL (Damien, 12 Sep 2026). The first
            build took "stated context" too far: `appearance-none` on a
            transparent background with no chevron rendered the product name as
            a 17px bold heading, so the one control that moves you around this
            tab looked like a title and nobody clicked it. The mock-up draws a
            purple chevron beside the name for exactly this reason. So the name
            keeps its weight, and the chevron, the hover tint, the pointer and
            the focus ring say it is pressable. */}
        <label className="group flex min-w-0 cursor-pointer flex-col gap-0.5">
          <span className="text-[11px] font-bold tracking-wider text-ks-grey-1 uppercase">
            Per-product facts — showing
          </span>
          <span className="relative flex min-w-0 items-center gap-1.5 rounded-ks-sm border border-ks-purple-3 bg-ks-surface py-0.5 pr-7 pl-2 group-hover:bg-ks-purple-4 has-[select:focus-visible]:ring-2 has-[select:focus-visible]:ring-ks-purple">
            <select
              className="min-w-0 cursor-pointer appearance-none truncate bg-transparent text-[17px] font-bold text-ks-ink outline-none"
              // Choosing WHICH product to look at is the primary way of moving
              // around this tab. The gate locked it, so a reader saw the first
              // product and could never reach any other — read-only became
              // no-access, which is the one thing the exemption exists to prevent.
              data-nonmutating
              value={selected ?? ""}
              onChange={(event) => onSelect(event.target.value)}
            >
              {products.map((product) => (
                <option key={product.product_key} value={product.product_key}>
                  {productLabel(product)}
                </option>
              ))}
              <option value={ALL_PRODUCTS}>Every product</option>
            </select>
            {/* The mock-up's chevron. `aria-hidden` because the `<select>` it
                decorates already announces itself as a combo box — a second
                announcement would name the control twice. */}
            <ChevronDown
              aria-hidden
              className="pointer-events-none absolute right-2 size-3.5 shrink-0 text-ks-purple"
            />
          </span>
          {/* The formula and the store's own identifiers, so the reader can
              tell WHICH thing on a shelf these numbers describe. */}
          <span className="truncate text-[13px] text-ks-grey-1">
            {all ? "Every product, side by side — the product column tells the rows apart." : subLine}
          </span>
        </label>

        <div className="grow" />

        <Button
          variant="outline"
          size="sm"
          // Switching scope reads a different slice; it writes nothing.
          data-nonmutating
          className="border-ks-purple-3 bg-ks-surface text-ks-purple"
          onClick={() => onSelect(all ? backToProduct(previous, products) : ALL_PRODUCTS)}
        >
          {all ? "Back to one product" : "Compare across products"}
        </Button>
      </div>

      {!complete && (
        // The catalogue read is paged, so this is now only reachable past its
        // ceiling — but the claim it makes is unchanged, and saying it beats
        // presenting a partial list as the whole catalogue.
        <p className="text-sm text-ks-grey-1">
          Built from the facts we could read — a product with no facts among them is not listed.
        </p>
      )}
    </div>
  );
}

/**
 * One family's table, heading and footnote.
 *
 * Exported for the SAME reason `EmptyPaneCard` and `CollapsibleFilters` are:
 * the screenshot harness renders it directly to photograph the SELECTED-row
 * treatment. Selection is `useState` inside `FactsTab`, so a static server
 * render can never reach a row that is open, and the purple ground plus brand
 * left edge would otherwise ship having never been looked at. Exporting the
 * real section beats a lookalike in the harness that would drift from it.
 */
export function FactSection({
  heading,
  headingHidden = false,
  headerHidden = false,
  ruled = false,
  caption,
  groups,
  showProduct,
  narrow = false,
  loading,
  emptyMessage,
  selected,
  onSelect,
  total,
  shown,
  footNote,
}: {
  heading: string;
  /** Kept for structure and for a screen reader, but not drawn twice. */
  headingHidden?: boolean;
  /** Passed straight to the table — see `DataTable`'s own note. */
  headerHidden?: boolean;
  /** A rule and a brand-coloured heading — "a different KIND of fact starts here". */
  ruled?: boolean;
  caption: string | null;
  groups: DomainGroup[];
  showProduct: boolean;
  /** The `narrow` layout: the same rows as a stacked list rather than a five-column table. */
  narrow?: boolean;
  loading: boolean;
  emptyMessage: string;
  selected: string | null;
  onSelect: (row: FactRow) => void;
  total: number | null;
  shown: number | null;
  /** The sentence that explains the shape of THIS table, under it. */
  footNote?: string;
}) {
  const rowCount = groups.reduce((sum, group) => sum + group.rows.length, 0);
  const columns = columnsFor(showProduct, onSelect, selected);

  return (
    <section className={ruled ? "mt-6 border-t-2 border-ks-grey-4 pt-3.5" : undefined}>
      {/* With the heading hidden, a caption still needs its own gap: it sat
          flush on the column captions and read as part of the header row. */}
      <div
        className={headingHidden ? (caption ? "pb-2" : undefined) : "flex flex-col gap-0.5 pb-1"}
      >
        <h2
          className={
            headingHidden
              ? "sr-only"
              : "text-xs font-bold tracking-wider text-ks-purple uppercase"
          }
        >
          {heading}
        </h2>
        {caption && <p className="text-[13px] text-ks-grey-1">{caption}</p>}
      </div>

      {loading ? (
        <DataTable
          columns={columns}
          rows={[]}
          page={{ pageIndex: 0, pageSize: 6, rowCount: 0 }}
          onPageChange={() => {}}
          density="catalogue"
          // The loaded table hides a repeated header; the skeleton of it must
          // too, or the first paint shows a header the settled screen does not.
          headerHidden={headerHidden}
          pending
        />
      ) : rowCount === 0 ? (
        <>
          <p className="pt-2 text-sm text-ks-ink">{emptyMessage}</p>
          {/* An empty section on a partial read has not shown the fact is
              absent — only that it is not among the facts loaded. */}
          {total !== null && shown !== null && shown < total && (
            <p className="pt-2 text-sm text-ks-grey-1">{truncationNotice(shown, total)}</p>
          )}
        </>
      ) : narrow ? (
        <>
          <NarrowFactList
            groups={groups}
            showProduct={showProduct}
            selected={selected}
            onSelect={onSelect}
          />
          {footNote && <p className="pt-2.5 text-xs text-ks-grey-1">{footNote}</p>}
          {total !== null && shown !== null && shown < total && (
            <p className="pt-2 text-sm text-ks-grey-1">{truncationNotice(shown, total)}</p>
          )}
        </>
      ) : (
        <>
          <div className="overflow-x-auto">
            {/* ONE table for every domain. A table per group repeats the whole
                Fact / Market / Value / Status / Last-checked header under each
                heading, which reads as several unrelated lists of one row. */}
            <DataTable
              columns={columns}
              groups={groups.map((group) => ({
                id: group.domain,
                title: group.title,
                rows: group.rows,
                // The section's own subject leads in the brand colour; a
                // company-wide grouping is subordinate to the rule above it.
                tone: ruled ? ("muted" as const) : ("primary" as const),
              }))}
              // The domain groups are a display grouping of rows already in
              // hand; the SECTION owns paging, so a group never paginates.
              page={{ pageIndex: 0, pageSize: Math.max(1, rowCount), rowCount }}
              onPageChange={() => {}}
              density="catalogue"
              rowClassName={rowTone(selected)}
              headerHidden={headerHidden}
            />
          </div>
          {footNote && <p className="px-3 pt-2.5 text-xs text-ks-grey-1">{footNote}</p>}
          {total !== null && shown !== null && shown < total && (
            <p className="px-3 pt-2 text-sm text-ks-grey-1">{truncationNotice(shown, total)}</p>
          )}
        </>
      )}
    </section>
  );
}

/**
 * The same rows, stacked, for a phone.
 *
 * A five-column table does not survive 390px. It was rendering there anyway —
 * Fact / Market / Value / Status / Last checked squeezed into a viewport that
 * fits about two of them — so the reader met a horizontal scrollbar before the
 * first value, and the mock-up's phone screen draws no column headers at all.
 *
 * Two lines per fact and the value on the right is the same information the
 * table carries, ordered for a thumb: the fact's name, then what is true about
 * it in one sentence, then the number. Nothing is dropped. Market, status and
 * age all live in the sub-line, and the whole row stays the same button — same
 * `data-nonmutating`, same `aria-current` — so the read-only gate and a screen
 * reader behave identically on both layouts.
 *
 * The domain heading stays: it is what says these three rows are nutrition and
 * the next two are shipping, which is the only grouping left once the table's
 * group rows are gone.
 */
function NarrowFactList({
  groups,
  showProduct,
  selected,
  onSelect,
}: {
  groups: DomainGroup[];
  showProduct: boolean;
  selected: string | null;
  onSelect: (row: FactRow) => void;
}) {
  return (
    <div className="flex flex-col gap-4 pt-1">
      {groups.map((group) => (
        <div key={group.domain} className="flex flex-col">
          <h3 className="pb-1 text-xs font-bold tracking-wider text-ks-purple uppercase">
            {group.title}
          </h3>
          {group.rows.map((row) => {
            const open = selected === row.id;
            const missing = row.status === "missing";
            return (
              <button
                key={row.id}
                type="button"
                // Opening a fact to read it changes nothing — the same marker
                // the table's own title button carries, for the same reason.
                data-nonmutating
                aria-current={open ? "true" : undefined}
                onClick={() => onSelect(row)}
                className={[
                  "flex min-h-14 items-center gap-3 border-b border-ks-grey-4 py-2.5 text-left",
                  open ? "border-l-[3px] border-l-ks-purple bg-ks-purple-4 pl-2" : "",
                  row.status === "superseded" ? "opacity-55" : "",
                ].join(" ")}
              >
                <span className="min-w-0 grow">
                  <span className="block text-[15px] leading-tight font-bold break-words text-ks-ink">
                    {row.title}
                  </span>
                  <span
                    className={`block truncate text-xs ${missing ? "text-ks-danger" : "text-ks-grey-1"}`}
                  >
                    {narrowSubLine(row, showProduct)}
                  </span>
                </span>
                <span
                  className={`shrink-0 text-[15px] ${missing ? "text-ks-grey-1" : "text-ks-ink"}`}
                >
                  {row.valueLabel}
                </span>
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}

/**
 * The one sentence under a fact's name on a phone.
 *
 * It carries the three columns the stacked layout drops — market, status and
 * age — in the order a person says them. A missing value leads with the word
 * `Missing`, because that is the whole point of the row and its market is not.
 */
function narrowSubLine(row: FactRow, showProduct: boolean): string {
  const lead = showProduct ? [row.product, row.market] : [row.market];
  if (row.status === "missing") return `${row.statusLabel} — ${row.lastChecked}`;
  if (row.status === "approved") return [...lead, `approved ${row.lastChecked}`].join(" · ");
  return [...lead, row.statusLabel].join(" · ");
}

/**
 * What the WHOLE row says about itself.
 *
 * The open row carries a tint and a brand edge rather than an underlined title:
 * a person glancing between the list and the pane needs to find the row from
 * across the screen, and a one-word underline is not findable at that distance.
 * A superseded row is dimmed — it is still readable, still selectable, and no
 * longer the answer.
 */
function rowTone(selected: string | null) {
  return (row: FactRow) => {
    if (selected === row.id) return "border-l-[3px] border-l-ks-purple bg-ks-purple-4";
    if (row.status === "superseded") return "opacity-55";
    return undefined;
  };
}

function columnsFor(
  showProduct: boolean,
  onSelect: (row: FactRow) => void,
  selected: string | null,
): ColumnDef<FactRow, unknown>[] {
  const columns: ColumnDef<FactRow, unknown>[] = [
    {
      id: "fact",
      header: "Fact",
      cell: ({ row }) => {
        const button = (
          <button
            type="button"
            // Opening a fact to READ it changes nothing, so it is not an edit
            // control: without this marker the shell's read-only gate makes it
            // inert and a `view` user cannot reach the detail pane or the audit
            // history they are entitled to.
            data-nonmutating
            // The open row is announced, not just tinted — `aria-current` is what
            // tells a screen-reader user which fact the pane beside them is about.
            aria-current={selected === row.original.id ? "true" : undefined}
            className="block w-full min-w-0 text-left"
            onClick={() => onSelect(row.original)}
          >
            {/* The human title leads; the machine key is a smaller second line,
                never the label a reader has to decode on every row.

                Both lines carry an EXPLICIT line-height. Left to inherit, the
                title took `text-sm`'s 1.25rem and the 11px key took it too, and a
                two-line cell measured ~66px against the mock-up's ~52px row —
                the single biggest density miss on S1.

                Both WRAP inside the capped measure rather than truncating: a
                name cut to "Energy per 100 g of prod…" cannot be read, and
                reading it is the point of the row (KAL-410). The table cell is
                `nowrap` by default, so `whitespace-normal` has to be said here. */}
            <span className="block max-w-[22rem] text-sm leading-tight font-bold break-words whitespace-normal text-ks-ink underline-offset-2 hover:underline">
              {row.original.title}
            </span>
            <span className="block max-w-[22rem] font-mono text-[11px] leading-4 break-all whitespace-normal text-ks-grey-1">
              {row.original.factKey}
            </span>
          </button>
        );
        // The Definition on hover, now that the title is the fact's short name
        // (KET-6326). One Radix tooltip on the button that already takes focus,
        // so a keyboard reaches it too; no native `title` beside it (the double
        // popup fixed on Last checked). No definition, no tooltip.
        if (!row.original.definition) return button;
        return (
          <Tooltip>
            <TooltipTrigger asChild>{button}</TooltipTrigger>
            <TooltipContent className="max-w-xs">
              <span className="font-bold">Definition</span> {row.original.definition}
            </TooltipContent>
          </Tooltip>
        );
      },
    },
  ];

  if (showProduct) {
    // PRODUCT LEADS, as the mock-up's all-products panel orders it: in this view
    // the product is the new question each row answers, and a fact name that
    // repeats down the first column read as a list of duplicates.
    columns.unshift({
      id: "product",
      header: "Product",
      cell: ({ row }) => (
        // Wraps inside a capped measure, like Value: a longer real product name
        // must not be what pushes the table past its column.
        //
        // Bold ink, as the mock-up sets it. In this view the product is the
        // question each row answers, and small grey type made it the quietest
        // word on the row (outcome-audit cycles 2 and 3, G4 / M1).
        <span className="inline-block max-w-[11rem] text-sm leading-tight font-bold whitespace-normal text-ks-ink">
          {row.original.product}
        </span>
      ),
    });
  }

  columns.push(
    {
      id: "market",
      header: "Market",
      cell: ({ row }) => <span className="text-[13px] text-ks-grey-1">{row.original.market}</span>,
    },
    {
      id: "value",
      header: "Value",
      // The one column a person reads DOWN rather than across, so it reads down
      // a shared right edge — "899" and "0.95" line up on their last digit.
      meta: { align: "right" },
      // Never a number without its unit, and never a blank where a value would
      // go: an unpublished value reads "Not set".
      //
      // It WRAPS, inside a capped measure. An ingredient list is a value too, and
      // "C8 MCT Oil 14,237 mg · C10 MCT Oil 7,616 mg" on one unbreakable line
      // widened the whole table past its column — a horizontal scrollbar on
      // every screen, from one row (KAL-355).
      cell: ({ row }) => (
        <span
          className={`inline-block max-w-[12rem] text-sm whitespace-normal ${row.original.status === "missing" ? "text-ks-grey-1" : "text-ks-ink"}`}
        >
          {row.original.valueLabel}
        </span>
      ),
    },
    {
      id: "status",
      header: "Status",
      cell: ({ row }) => <StatusBadge row={row.original} />,
    },
    {
      id: "last_checked",
      header: "Last checked",
      cell: ({ row }) =>
        row.original.approvedAt ? (
          // ONE tooltip, and one a keyboard can reach. The trigger carried a
          // native `title` with the same text as the Radix content, so a mouse
          // user got both popups stacked on the same word — and a bare `span`
          // takes no focus, so `TooltipTrigger`'s focus handlers never fired and
          // a keyboard user got neither. `tabIndex={0}` is what puts the exact
          // date on the tab order; dropping `title` is what stops the double.
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="text-[13px] text-ks-grey-1" tabIndex={0}>
                {row.original.lastChecked}
              </span>
            </TooltipTrigger>
            <TooltipContent>{exactDate(row.original.approvedAt)}</TooltipContent>
          </Tooltip>
        ) : (
          <span className="text-[13px] text-ks-grey-1">{row.original.lastChecked}</span>
        ),
    },
  );

  // One set of column shares whenever the product column is absent. That is
  // exactly when the company-wide table hides its header and relies on the
  // per-product table's captions above it — so the two must put every column in
  // the same place, or the lower table's Value and Status sit under nothing.
  // (With the product column the two tables differ and both headers show.)
  // Status is widest of the short columns: "Draft — not published" never wraps.
  if (!showProduct) {
    const shares: Record<string, string> = {
      fact: "32%",
      market: "14%",
      value: "17%",
      status: "19%",
      last_checked: "18%",
    };
    for (const column of columns) {
      const id = column.id as string;
      column.meta = { ...column.meta, width: shares[id] };
    }
  }

  return columns;
}

/**
 * Colour is never the only carrier — every badge says its state in words.
 *
 * A pill, not a rounded box: the status is a label ON the row, and a squared
 * chip at the same radius as the row's own corners reads as another cell.
 */
function StatusBadge({ row }: { row: FactRow }) {
  const tone =
    row.status === "missing"
      ? "bg-ks-surface text-ks-danger border-ks-danger"
      : row.status === "draft"
        ? "border-ks-purple-3 bg-ks-purple-4 text-ks-purple"
        : row.status === "superseded"
          ? "border-ks-grey-4 bg-ks-grey-4 text-ks-grey-1"
          : "border-ks-grey-4 bg-ks-grey-4 text-ks-ink";
  return (
    <span
      className={`inline-flex h-[22px] items-center rounded-full border px-2 text-xs whitespace-nowrap ${tone}`}
    >
      {row.statusLabel}
    </span>
  );
}

/**
 * The list could not be read — drawn as the mock-up's load-failure card, never
 * as an empty table (an empty table asserts "there is nothing", a different and
 * false claim).
 *
 * An ink headline says what failed, a grey line says what it does NOT mean, and
 * Try again sits on the right where a reader's eye ends the sentence.
 *
 * `messageFor` is rendered for every failure that says something SPECIFIC about
 * THIS one, and suppressed for the two that do not: a 503 and a non-`ProtexError`
 * both resolve to "Permission could not be confirmed, so nothing is shown",
 * which is the shell's permission state and a thing PROTEX never renders — a
 * list that failed to load is not a permission question.
 *
 * Suppressing by naming the one status to KEEP (403) rather than the two to
 * drop is what made this a regression against main: it silently swallowed 404,
 * 409 and 422 as well, so "Someone approved a different draft first. Reload to
 * see the current value." arrived as the generic card, with nothing to say
 * whether reloading was the fix or a waste of a click. The test that would
 * have caught it is the non-403 one below it.
 */
export function LoadFailureCard({
  areaName,
  error,
  onRetry,
}: {
  areaName: string;
  error: unknown;
  onRetry: () => void;
}) {
  const stated =
    error instanceof ProtexError && error.status !== 503 ? messageFor(error, areaName) : null;
  return (
    <AlertCard
      title={<>We could not load the facts for {areaName}.</>}
      detail={
        <>
          {stated !== null && `${stated} `}
          Nothing is missing — we just cannot show it right now. Counts above read
          “unavailable”, never zero.
        </>
      }
      action={
        <Button variant="outline" size="sm" data-nonmutating onClick={onRetry}>
          Try again
        </Button>
      }
    />
  );
}
